import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { Theme } from "@emotion/react";
import { mdsThemeHarvest } from "@/mds/themes/harvest";
import { mdsThemeHarvestMoon } from "@/mds/themes/harvest-moon";
import { MemCommonEditorTheme } from "@mem-labs/common-editor";

export enum ThemeType {
  Harvest = "harvest",
  HarvestMoon = "harvest-moon",
}

interface ThemeContextType {
  theme: Theme;
  themeType: ThemeType;
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeType;
  getPersistedTheme?: () => ThemeType | null;
  persistTheme?: (theme: ThemeType) => void;
}

export const ThemeContextProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = ThemeType.Harvest,
  getPersistedTheme,
  persistTheme,
}) => {
  const [themeType, setThemeType] = useState<ThemeType>(() => {
    const value = getPersistedTheme?.();
    if (value && (value === ThemeType.Harvest || value === ThemeType.HarvestMoon)) {
      return value as ThemeType;
    }
    return defaultTheme;
  });

  const theme = themeType === ThemeType.Harvest ? mdsThemeHarvest : mdsThemeHarvestMoon;

  // Persist theme changes to cookie
  useEffect(() => {
    persistTheme?.(themeType);
  }, [themeType, persistTheme]);

  // Set color-scheme CSS property on :root element based on theme
  useEffect(() => {
    const rootElement = document.documentElement;

    if (themeType === ThemeType.HarvestMoon) {
      // For "harvest-moon" theme, use dark mode
      rootElement.style.colorScheme = "dark";
    } else {
      // For "harvest" theme, use light mode
      rootElement.style.colorScheme = "light";
    }

    // Cleanup function to reset on unmount
    return () => {
      rootElement.style.colorScheme = "";
    };
  }, [themeType]);

  const toggleTheme = () => {
    setThemeType(prev => (prev === ThemeType.Harvest ? ThemeType.HarvestMoon : ThemeType.Harvest));
  };

  const setTheme = (newTheme: ThemeType) => {
    setThemeType(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    themeType,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeContextProvider");
  }
  return context;
};

export const useMemCommonEditorTheme = (): MemCommonEditorTheme => {
  const { themeType } = useTheme();
  return themeType === ThemeType.HarvestMoon
    ? MemCommonEditorTheme.HarvestMoon
    : MemCommonEditorTheme.Harvest;
};
